// Core domain model for Arlo's HQ.
//
// Everything that is shared between devices lives in a *collection* of
// records (see src/data/store.ts). Each collection holds one of the document
// shapes below. Records are merged last-writer-wins per record id, so every
// document is small and self-contained.

/** Local calendar date, YYYY-MM-DD. */
export type ISODate = string;
/** Epoch milliseconds. */
export type Millis = number;

export type Region = 'england' | 'scotland' | 'wales' | 'ni';
export type CockerType = 'working' | 'show' | 'unknown';

export interface Person {
  id: string;
  name: string;
  /** CSS colour used for avatars and "who did it" chips. */
  color: string;
  /** Has had a dog before — used to tune how much the app explains. */
  experienced?: boolean;
  /** Google account email — lets this person "Sign in with Google". */
  email?: string;
}

export interface HomeInfo {
  type: 'flat' | 'house';
  /** Floor number for flats (0 = ground). */
  floor?: number;
  lift?: boolean;
  /** Private garden, balcony or shared back court he can use. */
  outdoorSpace?: 'garden' | 'back-court' | 'balcony' | 'none';
  /** How long it takes to get from the door to his toilet spot, in minutes. */
  outsideMins?: number;
  /** Where he goes, e.g. "the grass by the church". */
  toiletSpot?: string;
  /** Home address — for the ID tag reminder and a sitter. Stored in the app, never in code. */
  address?: string;
  /** Home location — for weather, and to detect walks leaving/returning home. */
  lat?: number;
  lon?: number;
  /** Radius around home that counts as "at home", metres. */
  radiusM?: number;
}

/** Singleton `household` document in the `settings` collection. */
export interface Household {
  id: 'household';
  people: Person[];
  region: Region;
  /** 24h clock HH:MM — the kiosk dims and goes quiet in this window. */
  nightStart: string;
  nightEnd: string;
  /** Override default meal times, keyed by meals-per-day. */
  mealTimes?: Partial<Record<2 | 3 | 4, string[]>>;
  /** Where he sleeps, naps and nights (the wording on nap prompts). Unset reads as his crate. */
  den?: Den;
  /** Agreed toilet cue, shown on the toilet reminder. */
  toiletCue?: string;
  home?: HomeInfo;
  onboarded?: boolean;
  /** Anything a sitter should know (shown at the top of the sitter guide). */
  sitterNotes?: string;
  /** Who's on duty, per slot, by weekday (index 0 = Sunday). A night belongs to the evening it starts. */
  rota?: Partial<Record<RotaSlot, (string | null)[]>>;
  /** One-off swaps: key `YYYY-MM-DD:slot` (the date the slot belongs to) → person id. */
  rotaSwaps?: Record<string, string>;
  /** The overnight toilet trip while he's small. */
  /** Base address for opening camera clips, e.g. https://unifi.ui.com/consoles/… (UniFi Protect). */
  protectLink?: string;
  /** The FamilyWall list Arlo's shopping list syncs with. */
  familywall?: { listId: string; listName: string };
  nightTrip?: {
    /** Alarm time, HH:MM. Unset = the app's suggestion for his age. */
    time?: string;
    /** He's sleeping through — no more night trips. */
    off?: boolean;
  };
  /** Emailed insights reports (server/report.ts). */
  reports?: ReportConfig[];
}

export type ReportSection = 'summary' | 'sleep' | 'awake' | 'toilet' | 'training' | 'social' | 'goals' | 'headsups' | 'team' | 'health';

export interface ReportConfig {
  id: string;
  name: string;
  /** Daily covers yesterday; weekly covers the seven days before. */
  cadence: 'daily' | 'weekly';
  /** Weekly: the day it goes out (0 = Sunday). */
  weekday?: number;
  /** When it goes out, HH:MM. */
  time: string;
  to: string[];
  sections: ReportSection[];
  /** 'now': made on demand, so it runs up to this moment (today included) rather than to midnight. */
  upTo?: 'now';
  /** 2: chosen from the current list (with the awake hour and socialisation as their own sections). Unset: an older report. */
  v?: number;
  paused?: boolean;
}

/** Rota slots: the first trip of the morning, daytime (at home with him), the night trip. */
export type RotaSlot = 'morning' | 'day' | 'night';

export interface FoodConfig {
  brand?: string;
  product?: string;
  /** Current daily ration in grams from the packaging chart. */
  dailyGrams?: number;
  /** Energy density from the bag, kcal per 100 g — enables calorie maths. */
  kcalPer100g?: number;
  /** Size of the bag currently in use, kg. */
  bagKg?: number;
  /** Date the current bag was opened. */
  bagOpened?: ISODate;
  /** Food the breeder used, if different — drives the transition planner. */
  breederFood?: string;
  transitionStart?: ISODate;
}

/** Singleton `arlo` document in the `profile` collection. */
export interface Profile {
  id: 'arlo';
  name: string;
  breed: string;
  cockerType: CockerType;
  sex: 'male' | 'female';
  colour?: string;
  /** Date of birth (breeder's date). */
  dob?: ISODate;
  /** The day he comes home. */
  homeDate?: ISODate;
  /** When he actually came through the door (tapped on pick-up day). */
  arrivedAt?: Millis;
  microchip?: string;
  microchipDatabase?: string;
  /** His Crumb tag's public profile (what the QR code opens), if he has one. */
  crumbProfile?: string;
  kcRegisteredName?: string;
  insurer?: string;
  policyNumber?: string;
  /** Insurance terms, for claim nudges and the costs page. */
  insurance?: InsuranceTerms;
  /** The vet practice's health plan, if he's on one. */
  healthPlan?: HealthPlan;
  neutered?: boolean;
  /** Small JPEG data URL. */
  photo?: string;
  food: FoodConfig;
  notes?: string;
  /** Things to ask at the next vet visit (printed on the vet report). */
  vetQuestions?: string[];
  /** Running with him: when the vet said yes, and when the build-up began. */
  running?: { vetOk?: ISODate; planStart?: ISODate };
}

// ---------------------------------------------------------------------------
// Log events
// ---------------------------------------------------------------------------

export type Where = 'outside' | 'inside';
export type PooQuality = 'firm' | 'soft' | 'runny' | 'hard';
export type Appetite = 'all' | 'most' | 'some' | 'none';
export type Reaction = 'happy' | 'unsure' | 'worried';
/** Where he sleeps. */
export type Den = 'pen' | 'crate' | 'crate-in-pen';

/** The word for it in a sentence: "into his crate". */
export const denWord = (den?: Den) => (den === 'pen' ? 'pen' : 'crate');

export type SleepPlace = 'crate' | 'bed' | 'sofa' | 'lap' | 'car' | 'other';
export type GroomTask = 'brush' | 'ears' | 'nails' | 'teeth' | 'bath' | 'eyes' | 'feathers' | 'pro';
export type Symptom =
  | 'vomit'
  | 'diarrhoea'
  | 'not-eating'
  | 'lethargic'
  | 'itching'
  | 'ear-shaking'
  | 'limping'
  | 'coughing'
  | 'eye'
  | 'ate-something'
  | 'other';

export interface EventDataMap {
  wee: { where: Where };
  poo: { where: Where; quality?: PooQuality };
  meal: { grams?: number; appetite?: Appetite; meal?: number };
  water: Record<string, never>;
  treat: { what?: string };
  sleep: { place?: SleepPlace };
  walk: {
    distanceKm?: number;
    carried?: boolean;
    /** Simplified GPS trace, [lat, lng] rounded to ~1 m. */
    route?: [number, number][];
    /** Dog steps, if a tracker reported them. */
    steps?: number;
    /** Auto-detected from a phone leaving home: 'pending' until someone confirms Arlo went too. */
    auto?: 'pending' | 'confirmed';
    /** A run together rather than a walk. */
    run?: boolean;
  };
  /** Detections from a camera (e.g. UniFi Protect Alarm Manager). */
  camera: {
    /** `cry`: crying or whining (e.g. Reolink's crying detection). */
    kind: 'bark' | 'cry' | 'animal' | 'motion' | 'person' | 'sound';
    camera?: string;
    /** The camera's own event id (UniFi Protect), for a link to the clip. */
    eventId?: string;
    /** Link to the clip as the camera system gave it (may only work at home). */
    clip?: string;
    /** A frame captured at the time (served from /api/photos/:id?t=token). */
    snapshot?: string;
    snapshotToken?: string;
  };
  play: { kind?: string };
  /** A behaviour moment (content/behaviour.ts): "bitey", "zoomies"… — for spotting patterns. */
  behaviour: { kind: string };
  training: { skills: string[]; rating?: 1 | 2 | 3 };
  /** `visitId`: ticked off by a visit (the visit itself shows in the log, not this). */
  social: { itemId: string; reaction: Reaction; visitId?: string };
  /** Visitors at home: who (ids in the people collection), how he found it. Timed: at = arrived, end = left. */
  visit: { people: string[]; names: string[]; reaction?: Reaction };
  /** kg, plus optional body condition score (1–9, 4–5 ideal). */
  weight: { kg: number; bcs?: number };
  groom: { tasks: GroomTask[] };
  meds: { name: string; dose?: string };
  health: { symptom: Symptom; severity?: 1 | 2 | 3 };
  care: { careId: string; title: string };
  alone: { settled?: boolean };
  note: Record<string, never>;
  photo: { photoId: string };
  /** Daily totals pushed from an activity tracker (e.g. Tractive via Home Assistant). */
  tracker: {
    activeMin?: number;
    restMin?: number;
    daySleepMin?: number;
    nightSleepMin?: number;
    battery?: number;
    /** Distance covered that day, if the tracker reports it. */
    distanceKm?: number;
    steps?: number;
  };
}

export type EventType = keyof EventDataMap;

/** Where an event came from — tablet, phone, or an automation. */
export type EventSource = 'app' | 'nfc' | 'button' | 'automation' | 'import';

interface BaseEvent {
  id: string;
  at: Millis;
  /** End time for things with a duration (sleep, walks, alone time). */
  end?: Millis;
  by?: string;
  note?: string;
  source?: EventSource;
}

export type LogEvent<T extends EventType = EventType> = {
  [K in T]: BaseEvent & { type: K; data: EventDataMap[K] };
}[T];

// ---------------------------------------------------------------------------
// Care plan
// ---------------------------------------------------------------------------

export type CareKind =
  | 'vaccination'
  | 'worming'
  | 'flea'
  | 'vet'
  | 'admin'
  | 'grooming'
  | 'weigh'
  | 'training'
  | 'other';

export type Repeat =
  | { every: number; unit: 'day' | 'week' | 'month' }
  /** Every 2 weeks to 12 weeks old, then monthly (a monthly lungworm-covering product is the UK norm). */
  | { rule: 'puppyWorming' }
  /** Every 2 weeks to 6 months old, then monthly. */
  | { rule: 'puppyWeigh' };

export interface CareItem {
  id: string;
  kind: CareKind;
  title: string;
  /** Why this matters — shown to first-time owners. */
  why?: string;
  due?: ISODate;
  done?: ISODate;
  doneBy?: string;
  repeat?: Repeat;
  product?: string;
  notes?: string;
  /** Template key for generated items, so they can be regenerated safely. */
  template?: string;
  /** Hidden from lists (e.g. not relevant to this household). */
  dismissed?: boolean;
  /** Due date set by hand (e.g. the vet's booking) — never auto re-dated. */
  edited?: boolean;
}

// ---------------------------------------------------------------------------
// Everything else
// ---------------------------------------------------------------------------

export type ContactRole =
  | 'vet'
  | 'emergency-vet'
  | 'breeder'
  | 'insurer'
  | 'microchip'
  | 'groomer'
  | 'walker'
  | 'trainer'
  | 'sitter'
  | 'other';

export interface Contact {
  id: string;
  role: ContactRole;
  name: string;
  phone?: string;
  email?: string;
  address?: string;
  notes?: string;
}

/** Training progress per skill (id = skill id from content/training). */
export interface SkillProgress {
  id: string;
  level: 0 | 1 | 2 | 3 | 4;
  updatedBy?: string;
}

/** Agreed cue word overrides (id = cue id from content/cues). */
export interface CueWord {
  id: string;
  word: string;
  signal?: string;
  notes?: string;
}

/** House rules — decisions both owners agree on. */
export interface HouseRule {
  id: string;
  question: string;
  answer?: string;
  custom?: boolean;
}

/** Shopping list state; id = catalogue item id, or a random id if custom. */
export interface ShoppingItem {
  id: string;
  have: boolean;
  skip?: boolean;
  note?: string;
  /** Custom items only. */
  custom?: { name: string; category: string };
  /** On the shopping list to buy (synced to FamilyWall when that's set up). */
  list?: boolean;
  /** Our pick: the exact product we want, and where to get it. */
  product?: string;
  link?: string;
}

/** Who is at home, per person (from UniFi Wi-Fi presence or phone geofence). */
export interface Presence {
  /** person id */
  id: string;
  state: 'home' | 'away';
  since: Millis;
  source: 'wifi' | 'geofence' | 'manual';
}

export interface Photo {
  id: string;
  at: Millis;
  /** Small JPEG data URL (~360px) — travels with sync so galleries work offline. */
  data: string;
  /** The full-size image is on the server at /api/photos/:id. */
  full?: boolean;
  /** Per-photo link token for the full image (never the household key). */
  token?: string;
  /** This week's portrait (same spot, same angle — for the grow-up flipbook). */
  weekly?: boolean;
  /** Age in weeks when taken. */
  ageWeeks?: number;
  caption?: string;
  by?: string;
  /** Added from Google Photos (that item's id) — so picking it again doesn't duplicate it. */
  googleId?: string;
  /** Fingerprint of the original file — so adding or sharing it again doesn't duplicate it. */
  hash?: string;
}

/** A montage: a few photos laid out as one picture (the recipe — each phone draws it). */
export interface Montage {
  id: string;
  at: Millis;
  kind: 'week' | 'month' | 'growth' | 'custom';
  title: string;
  /** "12 Oct – 18 Oct", "8 to 16 weeks". */
  sub?: string;
  /** A line about the photos (written by Claude for the automatic ones). */
  caption?: string;
  /** Photo ids, in the order they're laid out. */
  photos: string[];
  /** Made by the app on Sunday evening, not by someone. */
  auto?: boolean;
  by?: string;
}

/**
 * One message in an Ask conversation. Conversations are shared by the
 * household: anyone can pick one up later or join in. The server writes
 * these (the question as it arrives, the answer as it streams), so they
 * appear on every phone.
 */
export interface AskMessage {
  id: string;
  /** Conversation id: messages with the same one are one conversation. */
  conv: string;
  role: 'user' | 'assistant';
  content: string;
  at: Millis;
  /** Who asked (person id). */
  by?: string;
  /** Their name as it was then ("Matt", "Sam (sitter)"). */
  name?: string;
  /** Asked out loud. */
  voice?: boolean;
  /** A photo sent with the question: a small JPEG data URL to show in the chat (the full one is kept on the server for the AI). */
  photo?: string;
  /** The answer is still being written. */
  streaming?: boolean;
  /** The answer failed (content says why). */
  error?: boolean;
}

/** A vet visit or phone call: what was said, done and prescribed. */
export interface VetVisit {
  id: string;
  /** When it happened. */
  at: Millis;
  kind: 'visit' | 'call';
  /** The practice (a Contact id), or a free-text name. */
  contactId?: string;
  practice?: string;
  /** Who they saw, e.g. "Dr Patel". */
  vet?: string;
  reason?: string;
  /** Weighed at the vet. */
  weightKg?: number;
  /** What the vet found and said. */
  notes?: string;
  /** Care-plan items done at this visit (ids), e.g. the 2nd vaccination. */
  careDone?: string[];
  /** Given or prescribed. */
  meds?: { /** Stable per row, so removing one never re-labels another. */ id?: string; name: string; dose?: string; instructions?: string; days?: number }[];
  /** Questions from the list, with the answers. */
  answers?: { q: string; a: string }[];
  followUp?: { date: ISODate; what: string };
  /** Pounds. */
  cost?: number;
  /** Worth claiming on the insurance. */
  claim?: boolean;
  /** The appointment this visit was booked as. */
  appointmentId?: string;
  by?: string;
}

export type AppointmentKind = 'vet' | 'vaccination' | 'class' | 'groomer' | 'other';

/** Something booked at a time: the vet, a puppy class, the groomer. */
export interface Appointment {
  id: string;
  /** Start time. */
  at: Millis;
  /** Expected length in minutes (default 30). */
  minutes?: number;
  kind: AppointmentKind;
  title: string;
  /** Where: a Contact id, or free text. */
  contactId?: string;
  place?: string;
  /** What to take or remember. */
  notes?: string;
  /** Who's going (person ids) — reminders go to them; none = everyone. */
  who?: string[];
  /** Care-plan items this appointment is for (e.g. the 2nd vaccination). */
  careIds?: string[];
  /** Their due dates before booking moved them (null = had none), so cancelling puts them back. */
  movedFrom?: Record<string, ISODate | null>;
  /** The vet visit written up afterwards. */
  visitId?: string;
  cancelled?: boolean;
  /** "No notes needed" after a vet appointment. */
  noWriteUp?: boolean;
  by?: string;
}

export interface InsuranceTerms {
  /** Excess per claim, pounds. */
  excess?: number;
  /** Share you pay after the excess, percent (e.g. 20 for older dogs). */
  coPayPercent?: number;
  /** Days after treatment to make a claim (check your policy; often 90–180). */
  claimDays?: number;
  /** Monthly premium, pounds. */
  monthly?: number;
  /** Policy start date. */
  start?: ISODate;
  /** Plan name, e.g. "Classic · Covered for Life". */
  plan?: string;
  /** Vet bills limit per policy year, pounds. */
  vetLimit?: number;
}

/** What a vet practice health plan (e.g. Vets4Pets Complete Care) pays for. */
export type HealthPlanBenefit = 'vaccinations' | 'flea-worm' | 'health-check' | 'vet-consults' | 'nurse-checks' | 'helpline' | 'microchip';

/** A monthly vet practice health plan: routine, preventative care (insurance covers illness and injury). */
export interface HealthPlan {
  provider: string;
  name?: string;
  monthly?: number;
  start?: ISODate;
  includes: HealthPlanBenefit[];
  /** Vet consultations included each year. */
  vetConsults?: number;
  /** Nurse checks (weight, nails) included each year. */
  nurseChecks?: number;
  helpline?: string;
  /** Minimum term, months. */
  minTermMonths?: number;
  notes?: string;
}

export type ExpenseCategory = 'vet' | 'food' | 'kit' | 'insurance' | 'training' | 'grooming' | 'care' | 'other';
/** `skip` = not claiming this one. */
export type ClaimStatus = 'to-claim' | 'submitted' | 'paid' | 'declined' | 'skip';

/** Money spent on him. */
export interface Expense {
  id: string;
  date: ISODate;
  /** Pounds. */
  amount: number;
  category: ExpenseCategory;
  title: string;
  /** Person id. */
  paidBy?: string;
  /** Set when this came from a vet visit's cost. */
  visitId?: string;
  notes?: string;
  claim?: { status: ClaimStatus; submitted?: ISODate; ref?: string; paid?: number };
  by?: string;
}

/** The training plan for one week (id = the Monday, YYYY-MM-DD). */
export interface TrainingWeek {
  id: ISODate;
  skills: string[];
}

/** A ticked item in the homecoming plan (id = plan item id). */
export interface PlanTick {
  id: string;
  done: boolean;
  by?: string;
  at?: Millis;
}

/** Someone he knows: family and friends who visit (from phone contacts or added by hand). */
export interface Visitor {
  id: string;
  name: string;
  /** Small JPEG data URL (from the phone's contact, or a photo). */
  photo?: string;
  relation?: 'family' | 'friend' | 'neighbour' | 'child' | 'helper' | 'other';
  /** Socialisation items (content/socialisation.ts, People) a visit from them counts towards. */
  traits?: string[];
  source?: 'contacts' | 'manual';
  addedAt?: Millis;
  /** Hidden from the visitor grid. */
  archived?: boolean;
}

/** Map of collection name → document type. */
export interface Collections {
  settings: Household;
  profile: Profile;
  events: LogEvent;
  care: CareItem;
  contacts: Contact;
  skills: SkillProgress;
  cues: CueWord;
  rules: HouseRule;
  shopping: ShoppingItem;
  photos: Photo;
  presence: Presence;
  homecoming: PlanTick;
  vetVisits: VetVisit;
  appointments: Appointment;
  expenses: Expense;
  trainingWeeks: TrainingWeek;
  montages: Montage;
  askMessages: AskMessage;
  people: Visitor;
}

export type CollectionName = keyof Collections;

export const COLLECTIONS: CollectionName[] = [
  'settings',
  'profile',
  'events',
  'care',
  'contacts',
  'skills',
  'cues',
  'rules',
  'shopping',
  'photos',
  'presence',
  'homecoming',
  'vetVisits',
  'appointments',
  'expenses',
  'trainingWeeks',
  'montages',
  'askMessages',
  'people',
];
